/**
 * batch-69 magazijn-cijfers (FO-520) — de telsessie: tellen, verschillen en
 * correcties doorvoeren met audittrail.
 *
 * Eén gedeeld request-schema per mutatie (API én formulier), DTO's voor de
 * schermen en de labels. Een correctie gaat altijd via `boekMutatieInTx`
 * (CORRECTIE_PLUS/CORRECTIE_MIN) en heeft een verplichte reden; boven de
 * vier-ogen-drempel (VoorraadInstelling) eerst een tweede goedkeuring.
 */

import { z } from 'zod'
import { VOORRAAD_CATEGORIE, VOORRAAD_HOOFDGROEP, type VoorraadCategorie, type VoorraadEenheid } from './voorraad'
import { queryBoolean } from './zod-query'

/**
 * `?toepassen=1` bij een Excel-import (tellijst, niveaus): doorvoeren in plaats van
 * alleen een preview. Via `queryBoolean`: `?toepassen=false` is echt uit.
 */
export const importToepassenQuerySchema = z.object({ toepassen: queryBoolean().default(false) })

// ─── Enums + labels ──────────────────────────────────────────────────────────

export const VOORRAAD_TELLING_SOORT = ['VOLLEDIG', 'DEELTELLING', 'STEEKPROEF'] as const
export type VoorraadTellingSoort = (typeof VOORRAAD_TELLING_SOORT)[number]
export const VOORRAAD_TELLING_SOORT_LABELS: Record<VoorraadTellingSoort, string> = {
  VOLLEDIG: 'Volledige telling',
  DEELTELLING: 'Deeltelling',
  STEEKPROEF: 'Steekproef',
}

export const VOORRAAD_TELLING_STATUS = ['OPEN', 'WACHT_GOEDKEURING', 'AFGEROND', 'GEANNULEERD'] as const
export type VoorraadTellingStatus = (typeof VOORRAAD_TELLING_STATUS)[number]
export const VOORRAAD_TELLING_STATUS_LABELS: Record<VoorraadTellingStatus, string> = {
  OPEN: 'Open',
  WACHT_GOEDKEURING: 'Wacht op goedkeuring',
  AFGEROND: 'Afgerond',
  GEANNULEERD: 'Geannuleerd',
}
export const VOORRAAD_TELLING_STATUS_BADGE: Record<VoorraadTellingStatus, 'warning' | 'info' | 'success' | 'secondary'> = {
  OPEN: 'info',
  WACHT_GOEDKEURING: 'warning',
  AFGEROND: 'success',
  GEANNULEERD: 'secondary',
}

export const VOORRAAD_TELLING_REGEL_STATUS = [
  'OPEN',
  'GETELD',
  'OVERGESLAGEN',
  'WACHT_GOEDKEURING',
  'DOORGEVOERD',
  'NIET_GECORRIGEERD',
] as const
export type VoorraadTellingRegelStatus = (typeof VOORRAAD_TELLING_REGEL_STATUS)[number]
export const VOORRAAD_TELLING_REGEL_STATUS_LABELS: Record<VoorraadTellingRegelStatus, string> = {
  OPEN: 'Nog te tellen',
  GETELD: 'Geteld',
  OVERGESLAGEN: 'Overgeslagen',
  WACHT_GOEDKEURING: 'Wacht op goedkeuring',
  DOORGEVOERD: 'Doorgevoerd',
  NIET_GECORRIGEERD: 'Niet gecorrigeerd',
}
export const VOORRAAD_TELLING_REGEL_STATUS_BADGE: Record<
  VoorraadTellingRegelStatus,
  'warning' | 'info' | 'success' | 'secondary' | 'danger'
> = {
  OPEN: 'secondary',
  GETELD: 'info',
  OVERGESLAGEN: 'secondary',
  WACHT_GOEDKEURING: 'warning',
  DOORGEVOERD: 'success',
  NIET_GECORRIGEERD: 'danger',
}

export const VOORRAAD_TELLING_REDEN = [
  'TELFOUT_EERDER',
  'BESCHADIGD_AFGESCHREVEN',
  'VERBRUIKT_NIET_GEBOEKT',
  'GEVONDEN',
  'DIEFSTAL_VERMIST',
  'VERKEERDE_LOCATIE',
  'OVERIG',
] as const
export type VoorraadTellingReden = (typeof VOORRAAD_TELLING_REDEN)[number]
export const VOORRAAD_TELLING_REDEN_LABELS: Record<VoorraadTellingReden, string> = {
  TELFOUT_EERDER: 'Eerdere telfout',
  BESCHADIGD_AFGESCHREVEN: 'Beschadigd, afgeschreven',
  VERBRUIKT_NIET_GEBOEKT: 'Verbruikt, niet geboekt',
  GEVONDEN: 'Gevonden',
  DIEFSTAL_VERMIST: 'Diefstal of vermist',
  VERKEERDE_LOCATIE: 'Verkeerde locatie',
  OVERIG: 'Overig (toelichting verplicht)',
}

/** Acties in `VoorraadTellingAudit.actie` (String-kolom). */
export const VOORRAAD_TELLING_AUDIT_ACTIE = [
  'AANGEMAAKT',
  'REGEL_GETELD',
  'REGEL_TOEGEVOEGD',
  'HERTELLING',
  'REDEN_GEZET',
  'INGEDIEND',
  'GOEDKEURING',
  'AFGEKEURD',
  'CORRECTIE_DOORGEVOERD',
  'OVERGESLAGEN',
  'NIET_GECORRIGEERD',
  'IMPORT',
  'AFGEROND',
  'GEANNULEERD',
  'OPMERKING_GEZET',
] as const
export type VoorraadTellingAuditActie = (typeof VOORRAAD_TELLING_AUDIT_ACTIE)[number]
export const VOORRAAD_TELLING_AUDIT_ACTIE_LABELS: Record<VoorraadTellingAuditActie, string> = {
  AANGEMAAKT: 'Telling aangemaakt',
  REGEL_GETELD: 'Geteld',
  REGEL_TOEGEVOEGD: 'Regel toegevoegd',
  HERTELLING: 'Hertelling',
  REDEN_GEZET: 'Reden vastgelegd',
  INGEDIEND: 'Ingediend voor tweede goedkeuring',
  GOEDKEURING: 'Tweede goedkeuring gegeven',
  AFGEKEURD: 'Tweede goedkeuring geweigerd',
  CORRECTIE_DOORGEVOERD: 'Correctie doorgevoerd',
  OVERGESLAGEN: 'Overgeslagen',
  NIET_GECORRIGEERD: 'Niet gecorrigeerd',
  IMPORT: 'Excel-import',
  AFGEROND: 'Telling afgerond',
  GEANNULEERD: 'Telling geannuleerd',
  OPMERKING_GEZET: 'Opmerking vastgelegd',
}

// ─── Request-schema's (gedeeld API + formulier) ─────────────────────────────

const idVeld = z.string().trim().min(1, 'Ongeldige id').max(64)

/** Wat er geteld wordt: locaties (leeg = alle magazijnen), onderdelen, bulk en/of uniek. */
export const voorraadTellingScopeSchema = z
  .object({
    locatieIds: z.array(idVeld).max(200).default([]),
    hoofdgroepen: z.array(z.enum(VOORRAAD_HOOFDGROEP)).max(VOORRAAD_HOOFDGROEP.length).default([]),
    categorieen: z.array(z.enum(VOORRAAD_CATEGORIE)).max(VOORRAAD_CATEGORIE.length).default([]),
    bulk: z.boolean().default(true),
    uniek: z.boolean().default(true),
  })
  .superRefine((v, ctx) => {
    if (!v.bulk && !v.uniek) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['bulk'], message: 'Kies bulk, uniek of allebei' })
    }
  })
export type VoorraadTellingScope = z.infer<typeof voorraadTellingScopeSchema>

export const maakTellingSchema = z.object({
  naam: z.string().trim().min(2, 'Minimaal 2 tekens').max(120, 'Maximaal 120 tekens'),
  soort: z.enum(VOORRAAD_TELLING_SOORT),
  /** Weggelaten: blind bij een volledige telling, anders niet (spec §B6). */
  blind: z.boolean().optional(),
  scope: voorraadTellingScopeSchema,
})
export type MaakTellingInput = z.infer<typeof maakTellingSchema>

const geteldAantalVeld = z
  .number({ error: 'Vul een aantal in' })
  .int('Alleen hele aantallen')
  .min(0, 'Minimaal 0')
  .max(1_000_000, 'Maximaal 1.000.000')

/**
 * Een telling invoeren op één regel. Bulk: `geteldAantal` (null = weer "niet
 * geteld", niet 0). Uniek: `aangetroffen` (null = weer open), met optioneel de
 * locatie waar hij lag. De opmerking mag altijd mee. `opnieuwGeteld`: bewust
 * opnieuw geteld (scanpagina), dus een nieuwe telling ook met hetzelfde aantal,
 * en een overgeslagen regel wordt alsnog geteld; vraagt een aantal of
 * "aangetroffen".
 */
export const telRegelSchema = z
  .object({
    geteldAantal: geteldAantalVeld.nullable().optional(),
    aangetroffen: z.boolean().nullable().optional(),
    aangetroffenLocatieId: idVeld.nullable().optional(),
    opmerking: z.string().trim().max(1000, 'Maximaal 1000 tekens').nullable().optional(),
    opnieuwGeteld: z.boolean().optional(),
  })
  .refine((v) => v.geteldAantal !== undefined || v.aangetroffen !== undefined || v.opmerking !== undefined, {
    message: 'Vul een aantal of "aangetroffen" in',
    path: ['geteldAantal'],
  })
  .refine((v) => !v.opnieuwGeteld || v.geteldAantal != null || v.aangetroffen != null, {
    message: 'Vul het getelde aantal in',
    path: ['geteldAantal'],
  })
export type TelRegelInput = z.infer<typeof telRegelSchema>

/** Scannen (mobiel): artikel- of exemplaar-QR op een locatie, optioneel met het getelde aantal. */
export const tellingScanSchema = z.object({
  token: z.string().trim().min(4, 'Ongeldige QR-code').max(200),
  locatieId: idVeld,
  geteldAantal: geteldAantalVeld.optional(),
})
export type TellingScanInput = z.infer<typeof tellingScanSchema>

const redenVelden = {
  reden: z.enum(VOORRAAD_TELLING_REDEN, { error: 'Kies een reden' }),
  toelichting: z.string().trim().max(1000, 'Maximaal 1000 tekens').nullable().optional(),
}

function overigVraagtToelichting(v: { reden?: VoorraadTellingReden; toelichting?: string | null }, ctx: z.RefinementCtx, pad: PropertyKey[] = []) {
  if (v.reden === 'OVERIG' && !(v.toelichting && v.toelichting.trim().length >= 3)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: [...pad, 'toelichting'], message: 'Licht "Overig" toe (minimaal 3 tekens)' })
  }
}

/** Reden vastleggen op een verschilregel (zonder door te voeren). */
export const tellingRedenSchema = z.object(redenVelden).superRefine((v, ctx) => overigVraagtToelichting(v, ctx))
export type TellingRedenInput = z.infer<typeof tellingRedenSchema>

/**
 * Doorvoeren (per regel, selectie of alles). Een reden kan hier mee; een regel
 * zonder reden (hier of eerder vastgelegd) wordt niet doorgevoerd. Open FO-467-
 * telverschillen op het artikel kunnen worden meegenomen (afwikkeling TELLING).
 */
export const tellingDoorvoerenSchema = z.object({
  regels: z
    .array(
      z
        .object({
          regelId: idVeld,
          reden: z.enum(VOORRAAD_TELLING_REDEN).optional(),
          toelichting: z.string().trim().max(1000).nullable().optional(),
          afwikkelVerschilIds: z.array(idVeld).max(50).optional(),
        })
        .superRefine((v, ctx) => overigVraagtToelichting(v, ctx)),
    )
    .min(1, 'Kies minimaal één regel')
    .max(500, 'Maximaal 500 regels per keer'),
})
export type TellingDoorvoerenInput = z.infer<typeof tellingDoorvoerenSchema>

/** De tweede goedkeuring (vier ogen) op een correctie boven de drempel. */
export const tellingBeslissingSchema = z
  .object({
    beslissing: z.enum(['GOEDKEUREN', 'AFKEUREN']),
    toelichting: z.string().trim().max(1000).nullable().optional(),
  })
  .superRefine((v, ctx) => {
    if (v.beslissing === 'AFKEUREN' && !(v.toelichting && v.toelichting.trim().length >= 3)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['toelichting'], message: 'Geef een reden (minimaal 3 tekens)' })
    }
  })
export type TellingBeslissingInput = z.infer<typeof tellingBeslissingSchema>

/** Overslaan (niet tellen) of niet corrigeren: altijd met reden. */
export const tellingRegelsMetRedenSchema = z.object({
  regelIds: z.array(idVeld).min(1, 'Kies minimaal één regel').max(500, 'Maximaal 500 regels per keer'),
  reden: z.string().trim().min(3, 'Geef een reden (minimaal 3 tekens)').max(1000, 'Maximaal 1000 tekens'),
})
export type TellingRegelsMetRedenInput = z.infer<typeof tellingRegelsMetRedenSchema>

/** Hertellen: de telling op de regels wissen (terug naar open). */
export const tellingHertellenSchema = z.object({
  regelIds: z.array(idVeld).min(1, 'Kies minimaal één regel').max(500, 'Maximaal 500 regels per keer'),
})
export type TellingHertellenInput = z.infer<typeof tellingHertellenSchema>

export const tellingAnnulerenSchema = z.object({
  reden: z.string().trim().min(3, 'Geef een reden (minimaal 3 tekens)').max(1000, 'Maximaal 1000 tekens'),
})
export type TellingAnnulerenInput = z.infer<typeof tellingAnnulerenSchema>

/** De vier-ogen-drempel en de herinnering (ADMIN of VOORRAAD.canApprove). */
export const tellingInstellingenSchema = z.object({
  tellingVierOgenBedrag: z
    .number({ error: 'Vul een bedrag in' })
    .min(0, 'Minimaal 0')
    .max(1_000_000, 'Maximaal 1.000.000')
    .multipleOf(0.01, 'Hooguit twee decimalen'),
  tellingVierOgenPercentage: z.number({ error: 'Vul een percentage in' }).int('Hele procenten').min(1, 'Minimaal 1').max(100, 'Maximaal 100'),
  tellingVierOgenMinSysteem: z.number({ error: 'Vul een aantal in' }).int('Hele aantallen').min(0, 'Minimaal 0').max(1_000_000),
  tellingOpenHerinneringDagen: z.number({ error: 'Vul een aantal dagen in' }).int('Hele dagen').min(1, 'Minimaal 1').max(365, 'Maximaal 365'),
})
export type TellingInstellingenInput = z.infer<typeof tellingInstellingenSchema>

export const listTellingenQuerySchema = z.object({
  status: z.enum(VOORRAAD_TELLING_STATUS).optional(),
  page: z.coerce.number().int().positive().default(1),
  pageSize: z.coerce.number().int().positive().max(100).default(25),
})
export type ListTellingenQuery = z.infer<typeof listTellingenQuerySchema>

export const TELLING_REGEL_WEERGAVE = ['ALLE', 'TE_TELLEN', 'GETELD', 'VERSCHILLEN', 'WACHT', 'AFGEHANDELD'] as const
export type TellingRegelWeergave = (typeof TELLING_REGEL_WEERGAVE)[number]
export const TELLING_REGEL_WEERGAVE_LABELS: Record<TellingRegelWeergave, string> = {
  ALLE: 'Alle regels',
  TE_TELLEN: 'Nog te tellen',
  GETELD: 'Geteld',
  VERSCHILLEN: 'Verschillen',
  WACHT: 'Wacht op goedkeuring',
  AFGEHANDELD: 'Afgehandeld',
}

export const tellingRegelsQuerySchema = z.object({
  weergave: z.enum(TELLING_REGEL_WEERGAVE).default('ALLE'),
  zoek: z.string().trim().max(100).optional(),
  locatieId: idVeld.optional(),
  soort: z.enum(['BULK', 'UNIEK']).optional(),
  page: z.coerce.number().int().positive().default(1),
  pageSize: z.coerce.number().int().positive().max(200).default(50),
})
export type TellingRegelsQuery = z.infer<typeof tellingRegelsQuerySchema>

// ─── DTO's ───────────────────────────────────────────────────────────────────

export interface VoorraadTellingVoortgangDTO {
  totaal: number
  /** Geteld of aangetroffen ingevuld (ook als het daarna is doorgevoerd). */
  geteld: number
  teTellen: number
  overgeslagen: number
  /** Geteld met een afwijking die nog niet is afgehandeld. */
  openVerschillen: number
  wachtGoedkeuring: number
  doorgevoerd: number
  nietGecorrigeerd: number
}

export interface VoorraadTellingListItemDTO {
  id: string
  nummer: string
  naam: string
  soort: VoorraadTellingSoort
  status: VoorraadTellingStatus
  blind: boolean
  scopeOmschrijving: string
  voortgang: VoorraadTellingVoortgangDTO
  aangemaaktDoorNaam: string | null
  createdAt: string
  afgerondOp: string | null
}

export interface VoorraadTellingRechtenDTO {
  magTellen: boolean
  magDoorvoeren: boolean
  /** VOORRAAD.canApprove: tweede goedkeuring (nooit op een eigen telling/indiening). */
  magGoedkeuren: boolean
  magAnnuleren: boolean
  /** Kostprijsrol: waarde-effect (verschil x prijs) zichtbaar. */
  magWaardeZien: boolean
  /** Blind tellen: het systeemaantal is voor deze gebruiker nu zichtbaar. */
  systeemZichtbaar: boolean
}

export interface VoorraadTellingTotalenDTO {
  regels: number
  verschillen: number
  doorgevoerd: number
  nettoStuks: number
  /** Netto waarde-effect van de doorgevoerde correcties (euro, alleen met kostprijsrol). */
  nettoWaarde: number | null
}

export interface VoorraadTellingDetailDTO extends VoorraadTellingListItemDTO {
  scope: VoorraadTellingScope
  snapshotOp: string
  afgerondDoorNaam: string | null
  geannuleerdOp: string | null
  annuleerReden: string | null
  totalen: VoorraadTellingTotalenDTO | null
  telrapportBeschikbaar: boolean
  /** Het telrapport in de bibliotheek (Document-id), na afronden. */
  telrapportDocumentId?: string | null
  rechten: VoorraadTellingRechtenDTO
  /** Locaties binnen de scope (voor het scannen en de filters). */
  locaties: Array<{ id: string; naam: string }>
  /** Wat afronden nog tegenhoudt (leeg = afronden kan). */
  afrondBlokkades: string[]
}

export interface VoorraadTellingRegelDTO {
  id: string
  soort: 'BULK' | 'UNIEK'
  status: VoorraadTellingRegelStatus
  artikelId: string
  artikelNummer: string
  artikelNaam: string
  categorie: VoorraadCategorie
  eenheid: VoorraadEenheid
  locatieId: string | null
  locatieNaam: string | null
  vaklocatie: string | null
  uniekItemId: string | null
  /** Serienummer / asset-tag (uniek). */
  identificatie: string | null
  /** null bij blind tellen voor wie het (nog) niet mag zien. */
  systeemAantal: number | null
  systeemStatus: string | null
  systeemHouder: string | null
  geteldAantal: number | null
  aangetroffen: boolean | null
  aangetroffenLocatieNaam: string | null
  geteldDoorNaam: string | null
  geteldOp: string | null
  opmerking: string | null
  /** Huidig saldo (bulk) - alleen als het systeemaantal zichtbaar is. */
  huidigSaldo: number | null
  /** Het saldo op het moment van tellen (bulk) - alleen als het systeemaantal zichtbaar is. */
  saldoBijTellen: number | null
  /** Geteld min het snapshot (bulk); null als niet geteld of niet zichtbaar. */
  verschil: number | null
  /**
   * Wat er bij doorvoeren geboekt wordt: geteld min het saldo bij tellen (bulk).
   * Een beweging na het tellen verandert dit niet.
   */
  correctieAantal: number | null
  saldoGewijzigdSindsSnapshot: boolean
  /** Bulk: wat er na het tellen geboekt is (+ of -, ter info; telt niet mee in de correctie). */
  mutatiesSindsTellen: number
  /** Uniek: het exemplaar is na het tellen in de app gewijzigd: eerst hertellen. */
  uniekGewijzigdSindsTellen: boolean
  /** Uniek: wat doorvoeren doet (VERMIST, VERPLAATSEN, INNEMEN, TERUG_OP_VOORRAAD) of null. */
  uniekVoorstel: 'VERMIST' | 'VERPLAATSEN' | 'INNEMEN' | 'TERUG_OP_VOORRAAD' | null
  /** Waarde-effect (alleen met kostprijsrol). */
  waardeEffect: number | null
  vereistVierOgen: boolean
  reden: VoorraadTellingReden | null
  toelichting: string | null
  ingediendDoorNaam: string | null
  goedgekeurdDoorNaam: string | null
  doorgevoerdOp: string | null
  mutatieId: string | null
  overgeslagenReden: string | null
  /** Open FO-467-telverschillen op dit artikel (meenemen bij doorvoeren). */
  openTelverschillen: Array<{ id: string; aantal: number; medewerkerNaam: string | null; reden: string }>
  /** Wacht op goedkeuring: de bij het indienen aangevinkte telverschillen (gaan mee bij goedkeuren). */
  teAfwikkelenVerschilIds: string[]
}

export interface VoorraadTellingRegelsMetaDTO {
  page: number
  pageSize: number
  total: number
  perWeergave: Record<TellingRegelWeergave, number>
}

export interface VoorraadTellingAuditDTO {
  id: string
  actie: string
  regelId: string | null
  artikelNaam: string | null
  /** Leesbare regel voor de tab Audit (bv. "Correctie -2 geboekt (saldo 9 naar 7)"). */
  samenvatting: string | null
  /** De ruwe waarden; zonder kostprijsrecht zonder waarde-gegevens. */
  oudeWaarde: unknown
  nieuweWaarde: unknown
  toelichting: string | null
  actorNaam: string
  createdAt: string
}

export interface VoorraadTellingScanResultaatDTO {
  soort: 'BULK' | 'UNIEK'
  regel: VoorraadTellingRegelDTO
  /** De regel bestond niet (artikel zonder saldo op deze locatie): toegevoegd met systeem 0. */
  toegevoegd: boolean
  melding: string | null
}

export interface TellingDoorvoerUitkomstDTO {
  doorgevoerd: number
  naarGoedkeuring: number
  overgeslagen: Array<{ regelId: string; reden: string }>
}

export interface TellingImportRegelDTO {
  rij: number
  tabblad: string
  sleutel: string
  status: 'BIJGEWERKT' | 'ONGEWIJZIGD' | 'FOUT'
  melding: string | null
}

export interface TellingImportResultaatDTO {
  toegepast: boolean
  regels: TellingImportRegelDTO[]
  aantalBijgewerkt: number
  aantalOngewijzigd: number
  aantalFouten: number
}

export interface TellingInstellingenDTO {
  tellingVierOgenBedrag: number
  tellingVierOgenPercentage: number
  tellingVierOgenMinSysteem: number
  tellingOpenHerinneringDagen: number
  magWijzigen: boolean
}

/**
 * "Laatst geteld" op het artikel-detail (FO-520 §C15): de laatste afgeronde
 * telling waarin het artikel geteld is, met het verschil.
 */
export interface VoorraadLaatstGeteldDTO {
  tellingId: string
  tellingNummer: string
  op: string
  verschil: number
}
